import { useState } from 'react';
import { Link, useLocation, useNavigate } from 'react-router-dom';
import { Logo } from './Logo';
import { LanguageToggle } from './LanguageToggle';

import {
  LayoutDashboard,
  User,
  LogOut,
  Menu,
  X,
  Plus,
  Store,
  FileText,
  ChevronDown,
  Receipt,
} from 'lucide-react';
import { useAuth } from '../contexts/AuthContext';
import { useTranslation } from 'react-i18next';
import { cn } from '../service/CoreFunctions';
import { Button } from './ui/button';
import { send } from './service';
import { useLoading } from '../hooks/useLoading';

export const AppLayout = ({ children }) => {
  const { user, setAuth } = useAuth();
  const { t } = useTranslation();
  const location = useLocation();
  const navigate = useNavigate();
  const [mobileMenuOpen, setMobileMenuOpen] = useState(false);
  const [openDesktopMenu, setOpenDesktopMenu] = useState(null);
  const [openMobileMenus, setOpenMobileMenus] = useState({});
  const { showLoading } = useLoading()

  const handleLogout = () => {
    logout();
    navigate('/login');
  };

  const logout = async () => {
    setAuth({});
    await send("io0013", {}, showLoading)
    sessionStorage.clear()
    localStorage.clear()
    navigate('/');
  }

  const navItems = [
    { path: '/invoice/create', label: t('createInvoice'), icon: Plus },
    ...(user?.status === 2 ? [{
      path: '/merchant/dashboard',
      label: t('dashboard'),
      icon: LayoutDashboard
    }] : []),
    {
      key: 'reports',
      label: t('report'),
      icon: FileText,
      children: [
        {
          path: '/report/invoice',
          label: t('invoiceReport'),
          icon: FileText,
        },
        {
          path: '/report/invoice-transaction',
          label: t('invoiceTranRpt'),
          icon: Receipt,
        },
      ],
    },
    { path: '/profile', label: t('profile'), icon: User },
  ];

  const isActive = (path) => location.pathname === path;

  const isParentActive = (item) => {
    if (!item?.children?.length) return false;
    return item.children.some((child) => location.pathname.startsWith(child.path));
  };

  const toggleDesktopMenu = (key) => {
    setOpenDesktopMenu((prev) => (prev === key ? null : key));
  };

  const toggleMobileMenu = (key) => {
    setOpenMobileMenus((prev) => ({
      ...prev,
      [key]: !prev[key],
    }));
  };

  return (
    <div className="min-h-screen bg-background">
      {/* Header */}
      <header className="sticky top-0 z-50 bg-card/80 backdrop-blur-lg border-b border-border">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          <div className="flex items-center justify-between h-16">
            <Logo />

            {/* Desktop Navigation */}
            <nav className="hidden md:flex items-center gap-1">
              {navItems.map((item) => {
                if (item.children?.length) {
                  const parentActive = isParentActive(item);
                  const isOpen = openDesktopMenu === item.key;

                  return (
                    <div key={item.key} className="relative">
                      <button
                        type="button"
                        onClick={() => toggleDesktopMenu(item.key)}
                        className={cn(
                          'flex items-center gap-2 px-4 py-2 rounded-lg text-sm font-medium transition-all duration-200',
                          parentActive
                            ? 'bg-primary/10 text-primary'
                            : 'text-muted-foreground hover:text-foreground hover:bg-muted'
                        )}
                      >
                        <item.icon className="h-4 w-4" />
                        {item.label}
                        <ChevronDown
                          className={cn(
                            'h-4 w-4 transition-transform duration-200',
                            isOpen ? 'rotate-180' : ''
                          )}
                        />
                      </button>

                      {isOpen && (
                        <div className="absolute left-0 top-full mt-2 w-72 rounded-xl border border-border bg-card shadow-lg p-2 z-50">
                          {item.children.map((child) => (
                            <Link
                              key={child.path}
                              to={child.path}
                              onClick={() => setOpenDesktopMenu(null)}
                              className={cn(
                                'flex items-center gap-3 px-4 py-3 rounded-lg text-sm font-medium transition-all duration-200',
                                isActive(child.path)
                                  ? 'bg-primary/10 text-primary'
                                  : 'text-muted-foreground hover:text-foreground hover:bg-muted'
                              )}
                            >
                              <child.icon className="h-4 w-4" />
                              {child.label}
                            </Link>
                          ))}
                        </div>
                      )}
                    </div>
                  );
                }

                return (
                  <Link
                    key={item.path}
                    to={item.path}
                    className={cn(
                      'flex items-center gap-2 px-4 py-2 rounded-lg text-sm font-medium transition-all duration-200',
                      isActive(item.path)
                        ? 'bg-primary/10 text-primary'
                        : 'text-muted-foreground hover:text-foreground hover:bg-muted'
                    )}
                  >
                    <item.icon className="h-4 w-4" />
                    {item.label}
                  </Link>
                );
              })}
            </nav>

            <div className="flex items-center gap-3">
              <LanguageToggle />

              {user?.businessName && (
                <div className="hidden md:flex items-center gap-2 px-3 py-1.5 bg-accent rounded-full">
                  <Store className="h-4 w-4 text-accent-foreground" />
                  <span className="text-sm font-medium text-accent-foreground">
                    {user.businessName}
                  </span>
                </div>
              )}

              <Button
                variant="ghost"
                size="sm"
                onClick={handleLogout}
                className="hidden md:flex text-muted-foreground hover:text-foreground"
              >
                <LogOut className="h-4 w-4 mr-2" />
                {t('logout')}
              </Button>

              {/* Mobile menu button */}
              <Button
                variant="ghost"
                size="icon"
                className="md:hidden"
                onClick={() => setMobileMenuOpen(!mobileMenuOpen)}
              >
                {mobileMenuOpen ? <X className="h-5 w-5" /> : <Menu className="h-5 w-5" />}
              </Button>
            </div>
          </div>
        </div>

        {/* Mobile Navigation */}
        {mobileMenuOpen && (
          <div className="md:hidden border-t border-border bg-card animate-slide-up">
            <nav className="px-4 py-4 space-y-1">
              {navItems.map((item) => {
                if (item.children?.length) {
                  const parentActive = isParentActive(item);
                  const isOpen = !!openMobileMenus[item.key];

                  return (
                    <div key={item.key} className="rounded-xl">
                      <button
                        type="button"
                        onClick={() => toggleMobileMenu(item.key)}
                        className={cn(
                          'flex items-center justify-between w-full px-4 py-3 rounded-xl text-sm font-medium transition-all duration-200',
                          parentActive
                            ? 'bg-primary/10 text-primary'
                            : 'text-muted-foreground hover:text-foreground hover:bg-muted'
                        )}
                      >
                        <span className="flex items-center gap-3">
                          <item.icon className="h-5 w-5" />
                          {item.label}
                        </span>
                        <ChevronDown
                          className={cn(
                            'h-4 w-4 transition-transform duration-200',
                            isOpen ? 'rotate-180' : ''
                          )}
                        />
                      </button>

                      {isOpen && (
                        <div className="mt-1 ml-3 space-y-1 border-l border-border pl-3">
                          {item.children.map((child) => (
                            <Link
                              key={child.path}
                              to={child.path}
                              onClick={() => setMobileMenuOpen(false)}
                              className={cn(
                                'flex items-center gap-3 px-4 py-3 rounded-xl text-sm font-medium transition-all duration-200',
                                isActive(child.path)
                                  ? 'bg-primary/10 text-primary'
                                  : 'text-muted-foreground hover:text-foreground hover:bg-muted'
                              )}
                            >
                              <child.icon className="h-4 w-4" />
                              {child.label}
                            </Link>
                          ))}
                        </div>
                      )}
                    </div>
                  );
                }

                return (
                  <Link
                    key={item.path}
                    to={item.path}
                    onClick={() => setMobileMenuOpen(false)}
                    className={cn(
                      'flex items-center gap-3 px-4 py-3 rounded-xl text-sm font-medium transition-all duration-200',
                      isActive(item.path)
                        ? 'bg-primary/10 text-primary'
                        : 'text-muted-foreground hover:text-foreground hover:bg-muted'
                    )}
                  >
                    <item.icon className="h-5 w-5" />
                    {item.label}
                  </Link>
                );
              })}

              <button
                onClick={handleLogout}
                className="flex items-center gap-3 px-4 py-3 rounded-xl text-sm font-medium text-muted-foreground hover:text-foreground hover:bg-muted w-full"
              >
                <LogOut className="h-5 w-5" />
                {t('logout')}
              </button>
            </nav>
          </div>
        )}
      </header>

      {/* Main Content */}
      <main className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 py-8">
        {children}
      </main>
    </div>
  );
};
